import React from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { Text } from "src/library/components/Text";
import { Input } from "src/library/components/Forms";
import { sentenceCase } from "src/library/utils";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";

import {
  SetUpdatingSettings,
  IFormValue as OrderSettingIFormValue
} from "../OrderSettings";
import AddNewMinimumOrderQuantitySetting from "./AddNewMinimumOrderQuantitySetting";

export interface MinimumOrderQuantitySettingProps {
  /** matching types with IFormValue["SettingElement"] value */
  setFieldValue: any;
  handleBlur: any;
  /** defining whats needed specifically for this setting and order setting setUpdatingSettings call back */
  setting: OrderSettingIFormValue<"minimum_order_quantity">;
  canAmend: boolean;
  setUpdatingSettings?: React.Dispatch<React.SetStateAction<SetUpdatingSettings>>;
  /** if the setting is inherited from a parent */
  inherited?: boolean;
}

export interface MinOrderQtySetting {
  code: string;
  minQuantity: number;
}

/**
 * Retruns a component that renders all set minimum order quantity values as well as
 * the ability to add new ones.
 * @returns
 */
const MinimumOrderQuantitySetting: React.FunctionComponent<MinimumOrderQuantitySettingProps> = ({
  setting,
  setFieldValue,
  handleBlur,
  canAmend,
  inherited,
  setUpdatingSettings
}) => {
  // Transform the data into a shape we can map over
  const minOrderQtySettings: MinOrderQtySetting[] = React.useMemo(() => {
    const transformedData = _.map(setting.value, (value, key) => ({
      code: key,
      minQuantity: value
    }));

    // Sort the array alphabetically, except for the "quantity" code which should always be first
    transformedData.sort((a, b) => {
      if (a.code === "quantity") return -1;

      if (b.code === "quantity") return 1;

      return a.code.localeCompare(b.code);
    });

    return transformedData;
  }, [ setting.value ]);

  return (
    <StyledWrapper>
      {minOrderQtySettings.length && minOrderQtySettings.map((minQtySetting, i) => (
        <div
          className="recorded-min-qty-container"
          key={`${setting.name}-${i}`}
        >
          <div className="item">
            <Text>
              {`${minQtySetting.code === "quantity" ? "Order" : sentenceCase(minQtySetting.code)} quantity:`}
            </Text>
          </div>

          <div className="item with-remove-icon">
            <Input
              value={minQtySetting.minQuantity}
              className="input"
              inputType="number"
              disabled={!canAmend}
              name={setting.name}
              autoHideLabels
              min="0"
              step="any"
              label="Minimum order quantity"
              handleChange={inputProps => {
                const updatedSetting = {
                  ...setting,
                  value: {
                    ...setting.value,
                    [ minQtySetting.code ]: inputProps.currentTarget.value
                  }
                };

                if (inherited !== undefined) {
                  updatedSetting.inherited = inherited;
                }

                setFieldValue(setting.name, updatedSetting);
                setUpdatingSettings && setUpdatingSettings({ minimum_order_quantity: "required" });
              }}
              handleBlur={handleBlur}
            />

            {minQtySetting.code !== "quantity" && (
              <div className="remove-qty-setting">
                <Icon
                  icon={Icons.CLOSE}
                  $fillColor="primaryDark"
                  onClick={() => {
                    const updatedMinQtySettings = { ...setting.value };

                    // Remove the setting from the object
                    delete updatedMinQtySettings[ minQtySetting.code ];

                    // Update the form value
                    setFieldValue(setting.name, {
                      ...setting,
                      value: updatedMinQtySettings
                    });
                    setUpdatingSettings && setUpdatingSettings({ minimum_order_quantity: "required" });
                  }}
                />
              </div>
            )}
          </div>

        </div>
      ))}

      <AddNewMinimumOrderQuantitySetting
        minQtySettings={setting.value}
        updateQtySettings={code => {
          const newSetting = {
            ...setting,
            value: {
              ...setting.value,
              // default to 1 for new min qty being added as 0 will remove the setting
              [ code ]: 1
            }
          };

          if (inherited !== undefined) {
            newSetting.inherited = inherited;
          }

          setFieldValue(setting.name, newSetting);
          setUpdatingSettings && setUpdatingSettings({ minimum_order_quantity: "required" });
        }}
      />

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .recorded-min-qty-container {
      display: flex;
      align-items: center;
      max-width: 400px;
      margin-bottom: ${theme.spacing.margins.sm};
    }

    .item {
      flex: 1;
    }

    .with-remove-icon {
      display: flex;
    }

    .remove-qty-setting {
      margin-left: ${theme.spacing.margins.xs};
      cursor: pointer;
      align-self: center;
    }
  `}
`;

export default MinimumOrderQuantitySetting;
